import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useQuery, useQueryClient } from "react-query";
import axios from "axios";
import { useContext } from "react";
import { FiRotateCcw } from "react-icons/fi";
import { AuthContext } from "../../../Context/AuthContext";
import PageHeader from "../../components/PageHeader";
import FilterBar from "../../components/FilterBar";
import DataTable from "../../components/DataTable";
import StatusBadge from "../../components/StatusBadge";
import Spinner from "../../../Spinner/Spinner";

const STATUS_FILTERS = ["all", "pending", "approved", "rejected", "resolved"];

const AdminReturnsPage = () => {
  const { token } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);

  const { data, isLoading, isError } = useQuery(
    ["adminReturns", page, statusFilter],
    async () => {
      const res = await axios.get(`${process.env.REACT_APP_SERVER_URL}/order/returns`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, status: statusFilter === "all" ? undefined : statusFilter },
      });
      return res.data;
    },
    { enabled: !!token, keepPreviousData: true }
  );

  const returns = useMemo(() => data?.returns || [], [data?.returns]);

  const handleStatus = async (returnId, status) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_SERVER_URL}/order/returns/${returnId}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Return updated");
      queryClient.invalidateQueries("adminReturns");
    } catch (err) {
      toast.error(err.response?.data?.message || "Update failed");
    }
  };

  const columns = [
    {
      key: "order",
      label: "Order",
      render: (row) => `#${row.order?.orderNumber || "—"}`,
    },
    {
      key: "customer",
      label: "Customer",
      render: (row) =>
        [row.user?.first_name, row.user?.last_name].filter(Boolean).join(" ") || row.user?.email || "—",
    },
    {
      key: "reason",
      label: "Reason",
      render: (row) => row.reason,
    },
    {
      key: "status",
      label: "Status",
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: "createdAt",
      label: "Requested",
      render: (row) => new Date(row.createdAt).toLocaleString(),
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <div className="dashboard-table-actions">
          {["approved", "rejected", "resolved"].map((status) => (
            <button
              key={status}
              type="button"
              className="dashboard-btn dashboard-btn--secondary"
              disabled={row.status === status}
              onClick={() => handleStatus(row._id, status)}
            >
              {status}
            </button>
          ))}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader title="Returns" subtitle="Manage customer return requests" icon={FiRotateCcw} />
      <FilterBar>
        <div className="dashboard-filter-pills">
          {STATUS_FILTERS.map((id) => (
            <button
              key={id}
              type="button"
              className={statusFilter === id ? "active" : ""}
              onClick={() => {
                setStatusFilter(id);
                setPage(1);
              }}
            >
              {id.charAt(0).toUpperCase() + id.slice(1)}
            </button>
          ))}
        </div>
      </FilterBar>
      {isLoading ? <Spinner /> : isError ? (
        <div className="dashboard-banner dashboard-banner--danger">Could not load returns.</div>
      ) : (
        <DataTable columns={columns} rows={returns.map((row) => ({ ...row, _key: row._id }))} />
      )}
    </>
  );
};

export default AdminReturnsPage;
